import {
  BrainSectionChartConfig,
  DashboardBrainPartCalculatedData,
  DashboardBrainPartCalculatedDataRelation,
  DashboardBrainPartTypicalRange,
  DashboardChartDataHorizontalLineConfig,
  DashboardChartDataSubmissionDetails,
  DashboardChartDataTypicalZonePoint,
  DashboardChartMetadataErrorIdentifier,
  DashboardChartMetadataStatus,
} from './dashboard-chart-data-general.symbols';
import {Submission} from '../../dashboard/symbols/submission.symbols';
import {DashboardBrainPart, DashboardBrainPartChartType} from '../../dashboard-brain-parts/symbols/dashboard-brain-parts-general.symbols';

/** Number of milliseconds in a year. */
export const millisecondsInYear = 31536000000;

/**
 * Describes a comparison result between the user's data and typical range.
 */
export enum DashboardChartDataUtilsComparisonResult {
  /** User's data is out of the typical range, but it is considered to be good. */
  AtypicalGood = 'atypical_good',

  /** User's data is in the typical range. */
  Typical = 'typical',

  /** User's data is out of the typical range, and it is considered to be bad. */
  AtypicalBad = 'atypical_bad',
}

/**
 * The typical zone is always defined by two percentage points.
 * Usually, it is 5% and 95%, so 0-4% considered as a 'Low' value,
 * 5-95% considered as a 'Typical' value, 96-100% considered as a 'High' value.
 *
 * But this can be inverted for some regions, like Ventricles.
 * Even for inverted, the typical zone will remain the same, but the legend will be inverted.
 * So, it will be 0-4% considered as a 'High' value, 5-95% considered as a 'Typical' value, 96-100% considered as a 'Low' value.
 */
export const firstTypicalZoneAgePercentagePoint = 5;

/**
 * The typical zone is always defined by two percentage points.
 * Usually, it is 5% and 95%, so 0-4% considered as a 'Low' value,
 * 5-95% considered as a 'Typical' value, 96-100% considered as a 'High' value.
 *
 * But this can be inverted for some regions, like Ventricles.
 * Even for inverted, the typical zone will remain the same, but the legend will be inverted.
 * So, it will be 0-4% considered as a 'High' value, 5-95% considered as a 'Typical' value, 96-100% considered as a 'Low' value.
 */
export const secondTypicalZoneAgePercentagePoint = 95;

/**
 * Defines the user's zone by comparing the user's percentage data with the typical range points.
 */
export const comparePercentageChartUtilsIsTypicalRange = (
  brainPart: DashboardBrainPart,
  brainPartCalculatedData: DashboardBrainPartCalculatedData
): DashboardChartDataUtilsComparisonResult => {
  switch (brainPart.chartType) {
    // For the typical chart, the lower zone is placed below the typical zone.
    // 0-4% considered as a 'Low' value, 5-95% considered as a 'Typical' value, 96-100% considered as a 'High' value.
    case DashboardBrainPartChartType.Typical: {
      if (brainPartCalculatedData?.percentOfUsersWithLowerRatio < firstTypicalZoneAgePercentagePoint) {
        return DashboardChartDataUtilsComparisonResult.AtypicalBad;
      } else if (brainPartCalculatedData?.percentOfUsersWithLowerRatio > secondTypicalZoneAgePercentagePoint) {
        return DashboardChartDataUtilsComparisonResult.AtypicalGood;
      } else {
        return DashboardChartDataUtilsComparisonResult.Typical;
      }
    }

    case DashboardBrainPartChartType.TypicalInverted: {
      // For the inverted chart, the lower zone is placed above the typical zone.
      // 0-4% considered as a 'High' value, 5-95% considered as a 'Typical' value, 96-100% considered as a 'Low' value.
      // Take note: the `secondTypicalZoneAgePercentagePoint` is used as a first point, because the legend is inverted.
      if (brainPartCalculatedData?.percentOfUsersWithLowerRatio > secondTypicalZoneAgePercentagePoint) {
        return DashboardChartDataUtilsComparisonResult.AtypicalBad;
      } else if (brainPartCalculatedData?.percentOfUsersWithLowerRatio < firstTypicalZoneAgePercentagePoint) {
        return DashboardChartDataUtilsComparisonResult.AtypicalGood;
      } else {
        return DashboardChartDataUtilsComparisonResult.Typical;
      }
    }
  }

  // The typical range comparison result can be calculated only for the chart with 'Typical type'.
  // It seems like the function was called in the wrong place.
  return null;
};

/**
 * Defines a closed zone coordinates using two lines points which are the limit for the zone.
 */
export const defineChartZoneCoordinatesByTwoLineCoordinates = (
  bottom: DashboardChartDataTypicalZonePoint[],
  upperLine: DashboardChartDataTypicalZonePoint[]
): DashboardChartDataTypicalZonePoint[] => {
  // Reverse the bottom line to create a closed zone.
  // It is needed to create a closed zone by two lines.
  // Example:
  // Let us have two lines defined by points ([x, y]):
  // upper: [[0, 7], [6, 7]]
  // bottom: [[0, 0], [6, 0]]
  // ================================
  // If we will try to draw a rectangle by these two lines ([...upper, ..bottom]), we will get a figure like this:
  // A____B
  // |    |
  //  \  /
  //   \/
  //   /\
  //  /  \
  // |____|
  // C    D
  // It is caused by the fact that the bottom line is defined by points from left to right.
  // ================================
  // So we need to reverse the bottom line to get a correct figure:
  // A____B
  // |    |
  // |    |
  // |    |
  // |    |
  // |    |
  // |____|
  // D    C
  return [...bottom, ...upperLine.slice().reverse()];
};

/**
 * Defines the range limits in milliseconds for the provided chart data.
 */
export const comparePercentageChartUtilsDefineRangeLimitsInMilliseconds = (
  upperLimitLinePoints: DashboardChartDataTypicalZonePoint[],
  bottomLimitLinePoints: DashboardChartDataTypicalZonePoint[],
  ageAtScan: number,
  dataOfScan: Date
): {upperLimitInMilliseconds: DashboardChartDataTypicalZonePoint[]; bottomLimitInMilliseconds: DashboardChartDataTypicalZonePoint[]} => {
  // Get the year start of the scan in milliseconds.
  // It is needed for calculating the age difference in milliseconds as the first point in range represents the start of the year as well.
  const startOfTheYearWithScan = new Date(dataOfScan.getFullYear(), 0, 1);
  const dataOfScanInMilliseconds = startOfTheYearWithScan.getTime();

  // Calculate the difference between the first point in the range and the age at the scan.
  const firstRangeAgePoint = upperLimitLinePoints[0][0];
  const ageDifference = ageAtScan - firstRangeAgePoint;
  const millisecondDifference = ageDifference * millisecondsInYear;

  // Calculate the first point of the typical range in milliseconds.
  const firstPointInMilliseconds = dataOfScanInMilliseconds - millisecondDifference;

  // Calculate the difference between the first and the second point in the range.
  const ageDifferenceNearBetweenPoints = upperLimitLinePoints[1][0] - upperLimitLinePoints[0][0];
  const millisecondDifferenceNearBetweenPoints = ageDifferenceNearBetweenPoints * millisecondsInYear;

  // Transform ranges based on age in the past years to the ranges based on the time in milliseconds.
  const upperLimitInMilliseconds = upperLimitLinePoints.map((point, index) => {
    return [firstPointInMilliseconds + millisecondDifferenceNearBetweenPoints * index, point[1]] as DashboardChartDataTypicalZonePoint;
  });
  const bottomLimitInMilliseconds = bottomLimitLinePoints.map((point, index) => {
    return [firstPointInMilliseconds + millisecondDifferenceNearBetweenPoints * index, point[1]] as DashboardChartDataTypicalZonePoint;
  });

  // Return the calculated limits.
  return {upperLimitInMilliseconds, bottomLimitInMilliseconds};
};

/**
 * Describes the data for defining chart zones.
 */
export interface ComparePercentageChartUtilsDefineChartZonesData {
  /** Limit for the upper line of the typical range. */
  upperLimitInMilliseconds: DashboardChartDataTypicalZonePoint[];

  /** Limit for the bottom line of the typical range. */
  bottomLimitInMilliseconds: DashboardChartDataTypicalZonePoint[];

  /** Brain part chart type. */
  brainPartChartType: DashboardBrainPartChartType;

  /** Bottom chart limit. */
  bottomChartLimit: number;

  /** Upper chart limit. */
  upperChartLimit: number;
}

/**
 * Define all necessary zones for the provided chart type.
 */
export const comparePercentageChartUtilsDefineChartZones = (
  comparePercentageData: ComparePercentageChartUtilsDefineChartZonesData
): {typicalZonePoints: DashboardChartDataTypicalZonePoint[]; lowerZonePoints: DashboardChartDataTypicalZonePoint[]} => {
  const leftChartLimit = comparePercentageData.upperLimitInMilliseconds[0][0];
  const rightChartLimit = comparePercentageData.upperLimitInMilliseconds[comparePercentageData.upperLimitInMilliseconds.length - 1][0];

  switch (comparePercentageData.brainPartChartType) {
    // For the common chart, the lower zone is placed below the typical zone.
    case DashboardBrainPartChartType.Typical: {
      // Create a closed zone by two lines related to typical range.
      const typicalZonePoints = defineChartZoneCoordinatesByTwoLineCoordinates(
        comparePercentageData.bottomLimitInMilliseconds,
        comparePercentageData.upperLimitInMilliseconds
      );

      // Define chart bottom line points.
      const lowerZoneSecondLinePoints: DashboardChartDataTypicalZonePoint[] = [
        [leftChartLimit, comparePercentageData.bottomChartLimit],
        [rightChartLimit, comparePercentageData.bottomChartLimit],
      ];

      // Create a closed zone by two lines related to lower range.
      const lowerZonePoints = defineChartZoneCoordinatesByTwoLineCoordinates(
        comparePercentageData.bottomLimitInMilliseconds,
        lowerZoneSecondLinePoints
      );

      return {typicalZonePoints, lowerZonePoints};
    }

    // For the inverted chart, the lower zone is placed above the typical zone.
    case DashboardBrainPartChartType.TypicalInverted: {
      // Create a closed zone by two lines related to typical range.
      const typicalZonePoints = defineChartZoneCoordinatesByTwoLineCoordinates(
        comparePercentageData.bottomLimitInMilliseconds,
        comparePercentageData.upperLimitInMilliseconds
      );

      // Define chart bottom line points.
      const lowerZoneSecondLinePoints: DashboardChartDataTypicalZonePoint[] = [
        [leftChartLimit, comparePercentageData.upperChartLimit],
        [rightChartLimit, comparePercentageData.upperChartLimit],
      ];

      // Create a closed zone by two lines related to lower range.
      const lowerZonePoints = defineChartZoneCoordinatesByTwoLineCoordinates(
        comparePercentageData.upperLimitInMilliseconds,
        lowerZoneSecondLinePoints
      );

      return {typicalZonePoints, lowerZonePoints};
    }
  }
};

/**
 * Describes needed data for defining chart related submission data.
 */
export interface DashboardChartDataSubmissionChartRelatedData {
  /** List of the good submissions related to the selected submission by type. */
  relatedSubmissions: Submission[];

  /** Selected brain part. */
  brainPart: DashboardBrainPart; // comes from a new not-existing API

  /** Selected submission id. */
  selectedSubmissionId: Submission['id']; // old good submissions state

  /** Fraction data for the selected submission. */
  brainPartFractionByScan: {[submissionId: string]: DashboardBrainPartCalculatedDataRelation}; // comes from an actual API, but a lot of data is skipped

  /** Typical range data for the selected submission. */
  brainPartTypicalRangeByTag: Map<string, DashboardBrainPartTypicalRange>;

  /** Defines whether the user has access to the graph. */
  userHasAccessToGraph: boolean;
}

/**
 * Defines chart related data for the selected submission.
 */
export const comparePercentageChartUtilsSubmissionChartRelatedData = (
  chartRelatedData: DashboardChartDataSubmissionChartRelatedData
): DashboardChartDataSubmissionDetails[] => {
  const submissionInfo: DashboardChartDataSubmissionDetails[] = [];

  // If the user has no access to the graph, return an empty array.
  if (!chartRelatedData.userHasAccessToGraph) {
    return submissionInfo;
  }

  // Define if the user's data is in the typical range and add it to the list.
  for (const submission of chartRelatedData.relatedSubmissions) {
    // Extract fraction & volume data for the current submission.
    const brainPartCalculatedDataRelation = chartRelatedData.brainPartFractionByScan[submission?.id];

    // Extract fraction data for the current brain part.
    const brainPartCalculatedData = brainPartCalculatedDataRelation?.[chartRelatedData.brainPart.name];

    // Define if the user's data is in the typical range.
    const comparisonResult = comparePercentageChartUtilsIsTypicalRange(chartRelatedData.brainPart, brainPartCalculatedData);

    // Add submission data to the list.
    submissionInfo.push({
      id: submission.id,
      is_public: submission.is_public,
      is_shared: submission.is_shared,
      ageAtScan: submission.age_at_scan,
      scanDate: new Date(submission.scan_date),
      volume: brainPartCalculatedData?.volume,
      fraction: brainPartCalculatedData?.fraction,
      isSelected: +chartRelatedData.selectedSubmissionId === +submission.id,
      scanQuality: submission.scan_quality,
      normativePercentile: brainPartCalculatedData?.percentOfUsersWithLowerRatio,
      comparisonResult,
    });
  }

  // Return the list of the submission data.
  return submissionInfo;
};

/**
 * Defines a shift for the chart's left limit.
 * It is used for defining how much space should be added to the minimal presented age.
 */
export const comparePercentageChartLeftLimitShift = 10;

/**
 * Defines a shift for the chart's right limit.
 * It is used for defining how much space should be added to the maximal presented age.
 */
export const comparePercentageChartRightLimitShift = 10;

/**
 * Defines a coefficient for the chart's bottom limit.
 * It is used for defining how much space should be added to min provided value.
 */
export const comparePercentageChartBottomLimitCoefficient = 0.8;

/**
 * Defines a coefficient for the chart's upper limit.
 * It is used for defining how much space should be added to max provided value.
 */
export const comparePercentageChartUpperLimitCoefficient = 1.2;

/**
 * There is no data for start and end point of values on Y chart axis.
 * The general idea is to take the min/max value and expand it by some coefficient to add some free space.
 */
export const comparePercentageChartUtilsCalculateYAxisLimits = (
  brainChartParameter: DashboardBrainPartTypicalRange,
  submissionChartData: DashboardChartDataSubmissionDetails[]
): {bottomChartLimit: number; upperChartLimit: number} => {
  // Calculate limits as expand of the typical range for some coefficient.
  const bottomChartLimit = brainChartParameter.minimalBottomLinePoint * comparePercentageChartBottomLimitCoefficient;
  const upperChartLimit = brainChartParameter.maximalUpperLinePoint * comparePercentageChartUpperLimitCoefficient;

  // Extract fraction data from the submission chart data.
  const fractionChartData = submissionChartData
    .map(submissionInfo => submissionInfo.fraction)
    .filter(fraction => Number.isFinite(fraction));

  // For cases when the user's data is out of the typical range, expand the limits where the origin is taken as the min/max user's value.
  let updatedBottomChartLimit = bottomChartLimit;
  let updatedUpperChartLimit = upperChartLimit;
  for (const fraction of fractionChartData) {
    if (updatedBottomChartLimit > fraction) {
      updatedBottomChartLimit = fraction * comparePercentageChartBottomLimitCoefficient;
    }
    if (updatedUpperChartLimit < fraction) {
      updatedUpperChartLimit = fraction * comparePercentageChartUpperLimitCoefficient;
    }
  }

  // Return the updated limits.
  return {bottomChartLimit: updatedBottomChartLimit, upperChartLimit: updatedUpperChartLimit};
};

/**
 * Calculates a chart's left and right limits.
 * The general idea is to have some shift before the first point and after the last point.
 */
export const comparePercentageChartUtilsCalculateXAxisLimits = (
  submissionChartRelatedData: DashboardChartDataSubmissionDetails[],
  minimalPresentedAge: number,
  maximalPresentedAge: number
): {leftChartLimit: number; leftAgeLimit: number; rightAgeLimit: number; rightChartLimit: number} => {
  // Sort the submission data by the scan date.
  submissionChartRelatedData.sort((a, b) => a.scanDate.getTime() - b.scanDate.getTime());

  // Define the earliest and the latest scan.
  const earliestScan = submissionChartRelatedData[0];
  const latestScan = submissionChartRelatedData[submissionChartRelatedData.length - 1];

  // Define the age at the earliest and the latest scan.
  const minAgeAtScan = earliestScan.ageAtScan;
  const maxAgeAtScan = latestScan.ageAtScan;

  // Calculate the limits for the chart.
  const calculatedLeftAgeLimit = minAgeAtScan - comparePercentageChartLeftLimitShift;
  const calculatedRightAgeLimit = maxAgeAtScan + comparePercentageChartRightLimitShift;

  // If the calculated limits are out of the possible limits, apply the possible limits.
  const comparedLeftAgeLimit = calculatedLeftAgeLimit < minimalPresentedAge ? minimalPresentedAge : calculatedLeftAgeLimit;
  const comparedRightAgeLimit = calculatedRightAgeLimit > maximalPresentedAge ? maximalPresentedAge : calculatedRightAgeLimit;

  // Calculate the applied shifts.
  const appliedLeftShift = comparedLeftAgeLimit - minAgeAtScan;
  const appliedRightShift = comparedRightAgeLimit - maxAgeAtScan;

  // Calculate the earliest and the latest year for the chart.
  const leftChartLimitYear = earliestScan.scanDate.getFullYear() - Math.abs(appliedLeftShift);
  const rightChartLimitYear = latestScan.scanDate.getFullYear() + Math.abs(appliedRightShift);

  // Define the limits for the chart.
  const leftChartLimit = new Date(leftChartLimitYear, 5, 30).getTime();
  const rightChartLimit = new Date(rightChartLimitYear, 5, 30).getTime();

  // Return the updated limits.
  return {
    leftAgeLimit: comparedLeftAgeLimit,
    rightAgeLimit: comparedRightAgeLimit,
    leftChartLimit: leftChartLimit,
    rightChartLimit: rightChartLimit,
  };
};

/**
 * Defines horizontal bars line configuration for the provided chart type.
 * @param brainPartChartType - currently active chart type.
 * @param upperLimitInMilliseconds - upper limit in milliseconds.
 * @param bottomLimitInMilliseconds - bottom limit in milliseconds.
 * @param upperChartLimit - upper chart limit.
 * @param leftChartLimit - left chart limit.
 */
export const comparePercentageChartUtilsDefineHorizontalLineConfig = (
  brainPartChartType: DashboardBrainPartChartType,
  upperLimitInMilliseconds: DashboardChartDataTypicalZonePoint[],
  bottomLimitInMilliseconds: DashboardChartDataTypicalZonePoint[],
  upperChartLimit: number,
  leftChartLimit: number
): DashboardChartDataHorizontalLineConfig[] => {
  // Extract presented age points from the typical range.
  const presentedAgePoints = upperLimitInMilliseconds.map(point => point[0]);

  // Find the nearest age point to the left chart limit.
  const nearestToTheLeftAgePointIndex = presentedAgePoints.findIndex(agePoint => agePoint >= leftChartLimit);
  const safeNearestPointIndex = nearestToTheLeftAgePointIndex === -1 ? presentedAgePoints.length - 1 : nearestToTheLeftAgePointIndex;

  // Extract the nearest point from the typical range.
  const minimalBottomLinePoint = bottomLimitInMilliseconds[safeNearestPointIndex][1];
  const maximalUpperLinePoint = upperLimitInMilliseconds[safeNearestPointIndex][1];

  // Calculate the difference between the upper and the bottom line.
  const pointDifference = maximalUpperLinePoint - minimalBottomLinePoint;

  switch (brainPartChartType) {
    // For the common chart, the horizontal bars line is placed in the direct order: low, typical, high.
    case DashboardBrainPartChartType.Typical: {
      return [
        {width: 0, height: minimalBottomLinePoint, color: '#EB5463', label: 'Low'},
        {
          width: 0,
          height: pointDifference,
          color: '#2A4AD4',
          label: 'Typical',
        },
        {width: 0, height: upperChartLimit - maximalUpperLinePoint, color: '#D5D6E3', label: 'High'},
      ];
    }

    // For the inverted chart, the horizontal bars line is placed in the inverted order: high, typical, low; the labels are renamed.
    case DashboardBrainPartChartType.TypicalInverted: {
      return [
        {width: 0, height: minimalBottomLinePoint, color: '#D5D6E3', label: '>5%'},
        {
          width: 0,
          height: pointDifference,
          color: '#2A4AD4',
          label: 'Typical',
        },
        {width: 0, height: upperChartLimit - maximalUpperLinePoint, color: '#EB5463', label: '>95%'},
      ];
    }

    // For the over time chart, the horizontal bars line is not needed.
    case DashboardBrainPartChartType.WM: {
      return [];
    }
  }
};

/**
 * Default empty values for the chart config.
 */
const comparePercentageChartDefaultConfigValues: BrainSectionChartConfig = {
  submissionChartRelatedData: [],
  bottomChartLimit: null,
  upperChartLimit: null,
  leftAgeLimit: null,
  rightAgeLimit: null,
  leftChartLimit: null,
  rightChartLimit: null,
  typicalZonePoints: [],
  lowerZonePoints: [],
  horizontalBarsLine: [],
  chartMetadata: {
    processStatus: DashboardChartMetadataStatus.ShouldBeHidden,
    errorIdentifier: null,
  },
};

/**
 * Defines the last error identifier for the chart.
 * Returns compatible chart config.
 */
export const comparePercentageChartUtilsProvideErrorStatus = (
  errorIdentifier: DashboardChartMetadataErrorIdentifier
): BrainSectionChartConfig => {
  return {
    ...comparePercentageChartDefaultConfigValues,
    chartMetadata: {
      processStatus: DashboardChartMetadataStatus.Error,
      errorIdentifier,
    },
  };
};

/**
 * Provides the chart config with the hidden status.
 */
export const comparePercentageChartUtilsProvideHiddenStatus = (): BrainSectionChartConfig => {
  return {
    ...comparePercentageChartDefaultConfigValues,
    chartMetadata: {
      processStatus: DashboardChartMetadataStatus.ShouldBeHidden,
      errorIdentifier: null,
    },
  };
};
